import * as React from 'react';
import { Link } from 'react-router';
import { push } from 'react-router-redux';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';

import API, { ThenArg } from '../../../../services/API';
import { getLoggedInMenu, URL_LOGIN, URL_SETS } from '../../../../constants';
import Searchform from '../Searchform';
import CategoriesToggleButton from './CategoriesToggleButton';
import { grid, leftPanelWidth } from '../../../../theme/libraries/grid';
import Dropdown from '../../../Dropdown/Dropdown';
import OnClickOutsideComponent from '../../../_helpers/OnClickOutsideComponent/OnClickOutsideComponent';
import { SideCart } from '../../../Cart/SideCart';
import {
  isCartOnlyDemand,
  isHomepageEshopEnabled,
  isSkvelkoHomepageEnabled,
  formatPrice,
  showPriceWithVat,
} from '../../../../utilities';
import {
  CaretDownIcon,
  CartIcon,
  ChevronRightIcon,
  HeartIcon,
  HomeIcon,
  HomeOutlineIcon,
  LockersIcon,
  SkvelkoIcon,
  UserIcon,
} from './icons';
import {
  audienceForUser,
  AUDIENCE_TABS,
  AUDIENCE_URLS,
  AudienceKey,
  buildSubmenu,
  buildUtilityLinks,
  detectActiveUtilityKey,
  detectAudienceStrict,
  isFurnitureSection,
  isSubmenuItemActive,
  loadAudienceCookie,
  saveAudienceCookie,
  MenuNode,
  SubmenuItem,
} from './menuData';

const NEAR_BLACK = '#171717';
const ROW_BORDER = '#d6d6d6';
const PILL_BG = '#fff5b7';
const PILL_BG_HOVER = '#ffee8f';
const PILL_TEXT = '#231f20';
const LOGO = '/images/logo.svg';
const SIDE_PADDING = grid.padding / 2;

const FORCED_LABEL_BREAKS: Record<string, string> = {
  'Nábytok a vybavenie škôl': 'Nábytok a<br />vybavenie škôl',
  'Aktuality pre školy': 'Aktuality<br />pre školy',
  'Pomôcky pre žiakov': 'Pomôcky<br />pre žiakov',
  'Služby pre školy': 'Služby<br />pre školy',
  'Výhody spolupráce': 'Výhody<br />spolupráce',
  'Referencie a realizácie': 'Referencie<br />a realizácie',
  'Zoznamy pomôcok Skvelko': 'Zoznamy pomôcok<br />Skvelko',
  'Zákaznícka karta': 'Zákaznícka<br />karta',
  'Darčeková karta': 'Darčeková<br />karta',
  'Naše výhody': 'Naše<br />výhody',
  'Podporujeme vzdelávanie': 'Podporujeme<br />vzdelávanie',
  'Výhody pre firmy': 'Výhody<br />pre firmy',
  'Karta výhod': 'Karta<br />výhod',
  'Doprava objednávok': 'Doprava<br />objednávok',
  'Reklamné predmety': 'Reklamné<br />predmety',
  'Potlač kalendárov a diárov': 'Potlač kalendárov<br />a diárov',
  'Značkové darčekové perá': 'Značkové<br />darčekové perá',
};

function menuLabelHtml(label: string): string {
  const plain = label.replace(/&nbsp;|\u00A0/g, ' ');
  return FORCED_LABEL_BREAKS[plain.trim()] || plain;
}

const COMPACT_RANGE = '@media (min-width: 992px) and (max-width: 1450px)';

interface HeaderNewProps {
  mainMenu: MenuNode[];
  user: any;
  cart: ThenArg<typeof API.getCart>;
  cartError?: any;
  deliveryFreePrice: number;
  settings: any;
  dispatch: any;
  currentPath: string;
  currentUniqs: string[];
  currentCategoryGroupId?: number;
  currentCategoryId?: number;
  categoriesGroup?: any[];
  favoriteProductsCount: number;
  queryAudience?: AudienceKey | null;
  searchDropdownVisible: boolean;
  searchedQuery: string;
  searchedResults: any[];
  searchedResultsLoader: boolean;
  onChangeSearchInput: (q: string) => void;
  onClickOutsideSearchForm: () => void;
  onRedirect: (url) => void;
  onSubmitSearchInput: (section: string) => void;
  onAppendCategories: () => void;
  onAppendProducts: () => void;
  onAppendWebContents: () => void;
  onSearchFocus: (isFocus: boolean) => void;
}

function HeaderNew(props: HeaderNewProps) {
  const {
    mainMenu,
    user,
    cart,
    cartError,
    deliveryFreePrice,
    settings,
    dispatch,
    currentPath,
    currentUniqs,
    currentCategoryGroupId,
    currentCategoryId,
    categoriesGroup,
    favoriteProductsCount,
    queryAudience,
  } = props;

  const [audience, setAudience] = React.useState<AudienceKey>(
    () =>
      queryAudience ??
      detectAudienceStrict(mainMenu, currentPath, currentUniqs) ??
      audienceForUser(user) ??
      'self',
  );
  const [cartOpen, setCartOpen] = React.useState(false);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const [audienceOverride, setAudienceOverride] = React.useState(false);

  React.useEffect(() => {
    if (!queryAudience) {
      return;
    }
    setAudience(queryAudience);
    saveAudienceCookie(queryAudience);
  }, [queryAudience]);

  React.useEffect(() => {
    if (
      queryAudience ||
      detectAudienceStrict(mainMenu, currentPath, currentUniqs) ||
      audienceForUser(user)
    ) {
      return;
    }
    const cookieAudience = loadAudienceCookie();
    if (cookieAudience) {
      setAudience(cookieAudience);
    }
  }, []);

  React.useEffect(() => {
    setAudienceOverride(false);
    if (queryAudience) {
      return;
    }
    const definite = detectAudienceStrict(mainMenu, currentPath, currentUniqs);
    if (definite) {
      setAudience(definite);
      saveAudienceCookie(definite);
    }
  }, [currentPath, mainMenu, currentUniqs, queryAudience]);

  React.useEffect(() => {
    const accountAudience = audienceForUser(user);
    if (!accountAudience || audienceOverride || queryAudience) {
      return;
    }
    if (detectAudienceStrict(mainMenu, currentPath, currentUniqs)) {
      return;
    }
    setAudience(accountAudience);
    saveAudienceCookie(accountAudience);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const activeUtilityKey = React.useMemo(
    () => detectActiveUtilityKey(mainMenu, currentPath, currentUniqs),
    [mainMenu, currentPath, currentUniqs],
  );
  const onUtilityTab = activeUtilityKey !== null && !audienceOverride;
  const isFurnitureActive = isFurnitureSection(
    categoriesGroup,
    currentCategoryGroupId,
    currentCategoryId,
  );
  const isEshopCategoryActive =
    ((currentCategoryGroupId ?? -1) > 0 || (currentCategoryId ?? -1) > 0) &&
    !isFurnitureActive;

  React.useEffect(() => {
    if (!isFurnitureActive || audienceOverride || queryAudience) {
      return;
    }
    if (detectAudienceStrict(mainMenu, currentPath, currentUniqs)) {
      return;
    }
    setAudience('schools');
    saveAudienceCookie('schools');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFurnitureActive]);

  const shellRef = React.useRef<HTMLDivElement>(null);
  const measureHeader = React.useCallback(() => {
    const el = shellRef.current;
    if (el && typeof document !== 'undefined' && el.offsetHeight > 0) {
      document.documentElement.style.setProperty(
        '--header-height',
        `${el.offsetHeight}px`,
      );
    }
  }, []);
  React.useEffect(() => {
    measureHeader();
  }, [measureHeader, audience, mainMenu, onUtilityTab]);
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const RO = (window as any).ResizeObserver;
    let ro: any = null;
    if (RO && shellRef.current) {
      ro = new RO(measureHeader);
      ro.observe(shellRef.current);
    }
    window.addEventListener('resize', measureHeader);
    return () => {
      if (ro) {
        ro.disconnect();
      }
      window.removeEventListener('resize', measureHeader);
    };
  }, [measureHeader]);

  const utilityLinks = React.useMemo(
    () => buildUtilityLinks(mainMenu),
    [mainMenu],
  );
  const skvelkoEnabled = isSkvelkoHomepageEnabled(settings);
  const eshopHomepage = isHomepageEshopEnabled(settings);
  const submenu: SubmenuItem[] = React.useMemo(
    () => buildSubmenu(mainMenu, audience, skvelkoEnabled, eshopHomepage),
    [mainMenu, audience, skvelkoEnabled, eshopHomepage],
  );

  const isOnlyDemand = isCartOnlyDemand(cart);
  const cartCount = cart && cart.items ? cart.items.length : 0;
  const showWithVat = showPriceWithVat(user);
  let cartTotalPrice = 0;
  if (cart && cart.total_items_price_with_discount && showWithVat) {
    cartTotalPrice = cart.total_items_price_with_discount;
  } else if (cart && cart.total_items_price_without_vat) {
    cartTotalPrice =
      cart.customer_card_discount && cart.customer_card_discount > 0
        ? cart.total_items_price_with_discount_without_vat || 0
        : cart.total_items_price_without_vat;
  }
  const showCartTotal = cartCount > 0 && !isOnlyDemand;

  const onClickFavorite = () => dispatch(push('/my-account/favorite-products'));

  return (
    <Shell ref={shellRef}>
      <TopBar>
        <Inner>
          <Tabs>
            {AUDIENCE_TABS.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <Tab
                  key={tab.key}
                  to={AUDIENCE_URLS[tab.key]}
                  className={
                    !onUtilityTab && audience === tab.key ? 'active' : ''
                  }
                  onClick={() => {
                    setAudience(tab.key);
                    setAudienceOverride(true);
                  }}
                >
                  <TabIcon className="tab-icon" />
                  <span>{tab.label}</span>
                </Tab>
              );
            })}
          </Tabs>
          <UtilityLinks>
            {utilityLinks.map((link) => {
              const LinkIcon = link.icon;
              return (
                <UtilityLink
                  key={link.key}
                  to={link.to}
                  onClick={() => setAudienceOverride(false)}
                  className={
                    onUtilityTab && link.key === activeUtilityKey
                      ? 'active'
                      : ''
                  }
                >
                  <LinkIcon className="util-icon" />
                  <span>{link.label}</span>
                </UtilityLink>
              );
            })}
          </UtilityLinks>
        </Inner>
      </TopBar>

      <MidBar>
        <Inner>
          <HeaderLeft>
            <LogoLink to={AUDIENCE_URLS[audience]}>
              <LogoImg src={LOGO} alt="Daffer.sk" />
            </LogoLink>
          </HeaderLeft>
          <SearchSlot>
            <Searchform
              searchDropdownVisible={props.searchDropdownVisible}
              searchedQuery={props.searchedQuery}
              searchedResults={props.searchedResults}
              searchedResultsLoader={props.searchedResultsLoader}
              onChangeSearchInput={props.onChangeSearchInput}
              onClickOutsideSearchForm={props.onClickOutsideSearchForm}
              onRedirect={props.onRedirect}
              onSubmitSearchInput={props.onSubmitSearchInput}
              onAppendCategories={props.onAppendCategories}
              onAppendProducts={props.onAppendProducts}
              onAppendWebContents={props.onAppendWebContents}
              onSearchFocus={props.onSearchFocus}
              user={user}
              cart={cart}
              settings={settings}
            />
          </SearchSlot>
          <Actions>
            {skvelkoEnabled ? (
              <SkvelkoButton
                to={
                  user && user.skvelko_type === 3
                    ? '/my-account/sets'
                    : URL_SETS
                }
              >
                {/* One span so the two lines stay a single flex item (the pill is
                    display:flex), broken exactly once by the <br>. */}
                <span>
                  {__('Zoznamy pomôcok')}
                  <br />
                  {__('Skvelko')}
                </span>
              </SkvelkoButton>
            ) : null}
            {user ? (
              <OnClickOutsideComponent
                onClickOutside={() => setAccountOpen(false)}
              >
                <AccountWrap
                  onMouseEnter={() => setAccountOpen(true)}
                  onMouseLeave={() => setAccountOpen(false)}
                >
                  <AccountPill
                    type="button"
                    className={accountOpen ? 'open' : ''}
                    onClick={() => setAccountOpen(true)}
                  >
                    <UserIcon className="account-icon" />
                    <AccountText>
                      <span>{__('Prihlásený')}</span>
                      {user.display_name ? (
                        <span className="name">{user.display_name}</span>
                      ) : null}
                    </AccountText>
                    <CaretDownIcon className="pill-caret" />
                  </AccountPill>
                  {accountOpen ? (
                    <AccountDropdown
                      items={getLoggedInMenu(user)}
                      onItemClick={() => setAccountOpen(false)}
                    />
                  ) : null}
                </AccountWrap>
              </OnClickOutsideComponent>
            ) : (
              <LoginPill to={URL_LOGIN}>
                <UserIcon className="account-icon" />
                <AccountText className="strong">
                  <span>{__('Prihlásenie')}</span>
                  <span>{__('Registrácia')}</span>
                </AccountText>
              </LoginPill>
            )}
            {user ? (
              <RoundButton type="button" onClick={onClickFavorite}>
                <HeartIcon className="action-icon" />
                {favoriteProductsCount ? (
                  <Badge>{favoriteProductsCount}</Badge>
                ) : null}
              </RoundButton>
            ) : null}
            <CartButton
              type="button"
              hasItems={showCartTotal}
              onClick={() => setCartOpen((o) => !o)}
            >
              <CartIcon className="action-icon" />
              {showCartTotal ? (
                <CartTotal>{formatPrice(cartTotalPrice, 'EUR')}</CartTotal>
              ) : null}
              {cartCount ? <Badge className="cart">{cartCount}</Badge> : null}
            </CartButton>
            <SideCart
              open={cartOpen}
              onClose={() => setCartOpen(false)}
              cart={cart}
              user={user}
              isOnlyDemand={isOnlyDemand}
              dispatch={dispatch}
              settings={settings}
              error={cartError}
              deliveryFreePrice={deliveryFreePrice}
            />
          </Actions>
        </Inner>
      </MidBar>

      {onUtilityTab ? null : (
        <BottomBar>
          <Inner>
            <HeaderLeft>
              <CategoriesToggleButton />
            </HeaderLeft>
            <SubMenu
              style={
                { '--submenu-cols': submenu.length } as React.CSSProperties
              }
            >
              {submenu.map((item) => {
                const active =
                  isSubmenuItemActive(item, currentPath, currentUniqs) ||
                  (item.key === 'eshop' && isEshopCategoryActive) ||
                  (item.key === 'furniture' && isFurnitureActive);
                const hasChildren = !!(item.children && item.children.length);
                return (
                  <SubMenuItem
                    key={item.key}
                    className={
                      item.isHome
                        ? 'home'
                        : item.key === 'eshop'
                        ? 'eshop'
                        : undefined
                    }
                    onMouseEnter={
                      hasChildren ? () => setOpenKey(item.key) : undefined
                    }
                    onMouseLeave={
                      hasChildren ? () => setOpenKey(null) : undefined
                    }
                  >
                    <SubMenuLink
                      to={item.to}
                      className={active ? 'active' : ''}
                      onClick={() => setOpenKey(null)}
                    >
                      {item.isHome ? (
                        active ? (
                          <HomeIcon className="menu-icon" />
                        ) : (
                          <HomeOutlineIcon className="menu-icon" />
                        )
                      ) : item.key === 'furniture' ? (
                        <LockersIcon className="menu-icon" />
                      ) : item.key === 'eshop' ? (
                        <CartIcon className="menu-icon" />
                      ) : item.key === 'skvelko' ? (
                        <SkvelkoIcon className="menu-icon" />
                      ) : null}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: menuLabelHtml(item.label),
                        }}
                      />
                    </SubMenuLink>
                    {hasChildren && openKey === item.key ? (
                      <MenuDropdown>
                        {item.children!.map((child, i) => (
                          <React.Fragment key={child.key}>
                            {i > 0 ? <DropdownDivider /> : null}
                            <DropdownLink
                              to={child.to}
                              onClick={() => setOpenKey(null)}
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: child.label,
                                }}
                              />
                              <ChevronRightIcon className="dropdown-arrow" />
                            </DropdownLink>
                          </React.Fragment>
                        ))}
                      </MenuDropdown>
                    ) : null}
                  </SubMenuItem>
                );
              })}
            </SubMenu>
          </Inner>
        </BottomBar>
      )}
    </Shell>
  );
}

const Shell = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.color.white};
  font-family: ${({ theme }) => theme.fonts.primary};
  box-shadow: 0 ${rem(4)} ${rem(8)} 0 rgba(0, 0, 0, 0.05);
`;

const Inner = styled.div`
  max-width: ${rem(1440)};
  width: 100%;
  margin: 0 auto;
  padding: 0 ${rem(SIDE_PADDING)};
  display: flex;
`;

const HeaderLeft = styled.div`
  flex: 0 0 auto;
  width: ${rem(leftPanelWidth)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TopBar = styled.div`
  background: ${({ theme }) => theme.color.primary};

  ${Inner} {
    align-items: flex-end;
  }
`;

const Tabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${rem(8)};
  /* Start where the SideMenu panel ends. */
  padding-left: ${rem(leftPanelWidth)};
  margin-top: ${rem(8)};

  ${COMPACT_RANGE} {
    /* Drop the 272px panel indent so the tabs start at the left edge and the
       top bar (tabs + utility links) fits without overflowing. */
    padding-left: ${rem(8)};
    gap: ${rem(2)};
  }
`;

const Tab = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  cursor: pointer;
  color: ${({ theme }) => theme.color.white};
  font-weight: 600;
  font-size: ${rem(16)};
  line-height: 1.25;
  padding: ${rem(8)} ${rem(24)} ${rem(16)};
  border-radius: ${rem(8)} ${rem(8)} 0 0;
  white-space: nowrap;
  text-decoration: none;

  .tab-icon {
    width: ${rem(20)};
    height: ${rem(20)};
    flex: 0 0 auto;
  }

  &:hover {
    color: ${({ theme }) => theme.color.white};
    background: rgba(255, 255, 255, 0.12);
    text-decoration: none;
  }

  &.active,
  &.active:hover {
    background: ${({ theme }) => theme.color.white};
    color: ${NEAR_BLACK};
  }

  ${COMPACT_RANGE} {
    font-size: ${rem(14)};
    gap: ${rem(6)};
    padding: ${rem(8)} ${rem(12)} ${rem(14)};

    .tab-icon {
      width: ${rem(18)};
      height: ${rem(18)};
    }
  }
`;

const UtilityLinks = styled.div`
  margin-left: auto;
  align-self: flex-end;
  display: flex;
  align-items: flex-end;
  gap: ${rem(8)};

  ${COMPACT_RANGE} {
    gap: ${rem(2)};
  }
`;

const UtilityLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(15)};
  line-height: 1.33;
  padding: ${rem(8)} ${rem(12)} ${rem(16)};
  border-radius: ${rem(8)} ${rem(8)} 0 0;
  white-space: nowrap;

  .util-icon {
    width: ${rem(20)};
    height: ${rem(20)};
    flex: 0 0 auto;
  }

  &:hover {
    color: ${({ theme }) => theme.color.white};
    background: rgba(255, 255, 255, 0.12);
    text-decoration: none;
  }

  &.active,
  &.active:hover {
    background: ${({ theme }) => theme.color.white};
    color: ${NEAR_BLACK};
  }

  ${COMPACT_RANGE} {
    font-size: ${rem(13)};
    gap: ${rem(6)};
    padding: ${rem(8)} ${rem(8)} ${rem(14)};

    .util-icon {
      width: ${rem(18)};
      height: ${rem(18)};
    }
  }
`;

const MidBar = styled.div`
  background: ${({ theme }) => theme.color.white};

  ${Inner} {
    align-items: center;
    padding-top: ${rem(8)};
    padding-bottom: ${rem(8)};
  }
`;

const LogoLink = styled(Link)`
  flex: 0 0 auto;
  display: block;
  width: ${rem(200)};
`;

const LogoImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const SearchSlot = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  /* Starts flush at the panel's right edge (so it lines up with the menu/tabs)
     and keeps the gap before the account/cart actions. */
  margin-right: ${rem(48)};

  ${COMPACT_RANGE} {
    /* Space is tight here and every px should go to the search field, so keep
       only a small gap before the action buttons. */
    margin-right: ${rem(8)};
  }
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`;

const SkvelkoButton = styled(Link)`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: ${rem(6)} ${rem(16)};
  border-radius: ${rem(999)};
  background: ${PILL_BG};
  color: ${PILL_TEXT};
  font-size: ${rem(15)};
  font-weight: 700;
  line-height: 1.2;
  /* Two-line label, split by an explicit <br> ("Zoznamy pomôcok" / "Skvelko");
     nowrap rules out any further wrapping, and the tight line-height + slim
     vertical padding keep the pill at the 48px action-row height. */
  white-space: nowrap;
  text-align: center;
  text-decoration: none;

  &:hover {
    background: ${PILL_BG_HOVER};
    color: ${PILL_TEXT};
    text-decoration: none;
  }

  ${COMPACT_RANGE} {
    font-size: ${rem(13)};
    padding: ${rem(6)} ${rem(12)};
  }
`;

/* 56×48 pill (figma): 8/12px padding around a 32px icon. */
const roundButton = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(8)} ${rem(12)};
  height: ${rem(48)};
  border-radius: ${rem(999)};
  border: none;
  cursor: pointer;
  flex: 0 0 auto;

  .action-icon {
    width: ${rem(32)};
    height: ${rem(32)};
  }
`;

const RoundButton = styled.button`
  ${roundButton};
  background: ${PILL_BG};
  color: ${PILL_TEXT};
  &:hover {
    background: ${PILL_BG_HOVER};
  }
`;

const CartButton = styled.button<{ hasItems: boolean }>`
  ${roundButton};
  background: ${({ theme }) => theme.color.primary};
  color: ${({ theme }) => theme.color.white};
  &:hover {
    background: ${({ theme }) => theme.color.primaryDark};
  }
  /* With priced items it grows into a pill: cart icon + total price. */
  ${({ hasItems }) =>
    hasItems &&
    css`
      padding: ${rem(8)} ${rem(20)} ${rem(8)} ${rem(14)};
      gap: ${rem(8)};
    `}
`;

const CartTotal = styled.span`
  font-size: ${rem(15)};
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  color: ${({ theme }) => theme.color.white};
`;

const Badge = styled.span`
  position: absolute;
  top: ${rem(-2)};
  right: ${rem(-2)};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${rem(18)};
  height: ${rem(18)};
  padding: 0 ${rem(5)};
  border-radius: ${rem(999)};
  background: ${({ theme }) => theme.color.secondary};
  color: ${NEAR_BLACK};
  font-size: ${rem(11)};
  font-weight: 500;
  line-height: 1;

  &.cart {
    top: ${rem(-7)};
    right: ${rem(-7)};
    min-width: ${rem(26)};
    height: ${rem(26)};
    font-size: ${rem(13)};
    background: ${({ theme }) => theme.color.secondary};
    color: ${NEAR_BLACK};
    border: ${rem(2)} solid ${({ theme }) => theme.color.white};
  }
`;

const pillBase = css`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  background: ${PILL_BG};
  border: none;
  border-radius: ${rem(999)};
  padding: ${rem(8)} ${rem(20)} ${rem(8)} ${rem(12)};
  cursor: pointer;
  color: ${NEAR_BLACK};
  text-decoration: none;
  flex: 0 0 auto;

  .account-icon {
    width: ${rem(32)};
    height: ${rem(32)};
    flex: 0 0 auto;
  }

  .pill-caret {
    width: ${rem(24)};
    height: ${rem(24)};
    flex: 0 0 auto;
    transition: transform 0.15s ease;
  }

  &:hover {
    background: ${PILL_BG_HOVER};
    text-decoration: none;
  }
`;

const AccountText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: ${rem(14)};
  line-height: 1.15;
  color: ${NEAR_BLACK};

  ${COMPACT_RANGE} {
    font-size: ${rem(12)};
  }

  /* Long account names are cut with an ellipsis, like the figma pill. */
  .name {
    max-width: ${rem(150)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.strong span {
    font-weight: 600;
  }
`;

const LoginPill = styled(Link)`
  ${pillBase};
`;

const AccountWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const AccountPill = styled.button`
  ${pillBase};
  font-family: inherit;

  &.open .pill-caret {
    transform: rotate(180deg);
  }

  ${COMPACT_RANGE} {
    /* Space is tight — trim the empty strip after the caret. */
    padding-right: ${rem(10)};
  }
`;

const AccountDropdown = styled(Dropdown)`
  display: block;
  width: ${rem(280)};
  top: calc(100% + ${rem(8)});
  right: 0;
  /* Transparent bridge over the 8px gap to the pill, so moving the cursor from
     the pill onto the menu stays within AccountWrap and doesn't close it. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -${rem(8)};
    height: ${rem(8)};
  }
`;

const BottomBar = styled.div`
  background: ${({ theme }) => theme.color.white};
  border-bottom: ${rem(1)} solid ${ROW_BORDER};

  /* 64px row (figma) so the centred toggle button gets ~12px above/below. */
  ${Inner} {
    min-height: ${rem(64)};
  }

  ${COMPACT_RANGE} {
    /* Shrink the categories-toggle column (and left-align it) so the menu —
       which is long in the "Pre školské zariadenia" tab — can extend further to
       the left and fit. The toggle button shrinks to match and wraps its label
       to two lines (see CategoriesToggleButton), so the column can be narrow. */
    ${HeaderLeft} {
      width: ${rem(96)};
      justify-content: flex-start;
    }
  }
`;

const SubMenu = styled.nav`
  /* Every audience tab lays its menu out as a grid. Columns are sized to their
     content (long items get more room, short items less), minmax(0, …) lets a
     column shrink below its content when space is tight (long labels then wrap
     inside the cell), and any leftover space stretches the auto columns
     equally, so the menu always fills the full row width. Column count comes
     from --submenu-cols (set inline from submenu.length). */
  display: grid;
  grid-template-columns: repeat(var(--submenu-cols, 10), minmax(0, auto));
  flex: 1 1 auto;
  min-width: 0;
  align-items: stretch;
  gap: ${rem(4)};

  ${COMPACT_RANGE} {
    column-gap: ${rem(6)};
  }
`;

const SubMenuItem = styled.div`
  position: relative;
  min-width: 0;
  display: flex;
  align-items: stretch;
`;

const SubMenuLink = styled(Link)`
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(10)};
  padding: ${rem(6)};
  color: ${NEAR_BLACK};
  font-size: ${rem(15)};
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  border-bottom: ${rem(3)} solid transparent;

  > span {
    min-width: 0;
  }

  .menu-icon {
    width: ${rem(24)};
    height: ${rem(24)};
    flex: 0 0 auto;
  }

  /* Hover underline: thin 1px red line at the bottom edge (per Figma). */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -${rem(3)};
    height: ${rem(1)};
    background: ${({ theme }) => theme.color.primary};
    opacity: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: none;
  }

  &:not(.active):hover::after {
    opacity: 1;
  }

  &.active {
    color: ${({ theme }) => theme.color.primary};
    border-bottom-color: ${({ theme }) => theme.color.secondary};
  }

  ${COMPACT_RANGE} {
    font-size: ${rem(13)};
    gap: ${rem(6)};

    .menu-icon {
      width: ${rem(20)};
      height: ${rem(20)};
    }
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 30;
  width: ${rem(320)};
  max-width: 90vw;
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(8)};
  box-shadow: 0 ${rem(8)} ${rem(48)} 0 rgba(0, 0, 0, 0.25);
  padding: ${rem(8)};
  text-align: left;
`;

const DropdownLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  padding: ${rem(12)};
  border-radius: ${rem(4)};
  color: ${NEAR_BLACK};
  font-size: ${rem(15)};
  font-weight: 400;
  line-height: 1.4;
  text-decoration: none;

  .dropdown-arrow {
    width: ${rem(16)};
    height: ${rem(16)};
    flex: 0 0 auto;
    color: ${NEAR_BLACK};
  }

  &:hover {
    background: #f7f7f7;
    color: ${({ theme }) => theme.color.primary};
    text-decoration: none;
  }
`;

const DropdownDivider = styled.div`
  height: ${rem(1)};
  background: #e0e0e0;
`;

export default HeaderNew;
